import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppContext } from "../../classes/contexts/AppContext";
import { useNotification } from "../../classes/contexts/NotificationContext";
import { usePatientContext } from "../../classes/contexts/PatientContext";
import { useReportContext } from "../../classes/contexts/ReportContext";
import { ReportFormat } from "../../classes/enums/ReportFormatEnum";
import { SnackbarType } from "../../classes/enums/SnackbarTypeEnum";
import Button from "../../components/button/Button";
import { MedicalImageThumbnail } from "../../components/medicalImageThumbnail/MedicalImageThumbnail";
import RadioButtonFlat from "../../components/radioButtons/RadioButtonFlat";
import Select from "../../components/select/Select";
import TextArea from "../../components/textarea/TextArea";
import useTranslation from "../../hooks/i18n/useTranslation";
import { useReportData } from "../../hooks/useReportData";
import { downloadReport, prepareReport } from "../../services/ReportService";
import { uploadAnnexeImageEndpoint } from "../../services/api/Images";
import { buildReportPayload } from "../../utils/ReportPayloadBuilder";
import ModalBase from "../common/uploadModal/modalBase/ModalBase";
import "./GenerateReportPopup.css";

const formatOrientationLabel = orientation => {
  if (!orientation) return "";
  const lower = orientation.toLowerCase();
  if (lower.includes("sag")) return "Sagittal";
  if (lower.includes("cor")) return "Coronal";
  if (lower.includes("ax")) return "Axial";
  if (lower.includes("lat")) return "Lateral";
  return orientation.charAt(0).toUpperCase() + orientation.slice(1);
};

const isTomographicModality = modality => {
  if (!modality) return false;
  const upper = modality.toUpperCase();
  return upper === "CT" || upper === "MR" || upper === "MRI";
};

const buildSliceInfo = (metadata, modality) => {
  if (!isTomographicModality(modality)) {
    return null;
  }

  const current =
    metadata?.actualSliceNumber ?? metadata?.sliceData?.current ?? null;
  const max = metadata?.sliceData?.max ?? null;

  if (current === null || max === null) {
    return null;
  }

  return {
    current,
    max,
  };
};


const getAnnexId = (annex, fallback) =>
  annex?.id ||
  annex?._id ||
  annex?.fileName ||
  annex?.url ||
  annex?.metadata?.name ||
  annex?.metadata?.displayName ||
  fallback;

const GenerateReportPopup = () => {
  const { hidePopup } = useAppContext();
  const { addNotification, updateNotification, removeNotification } =
    useNotification();
  const { refreshReports } = usePatientContext();
  const { sections, REPORT_SECTIONS } = useReportContext();
  const reportData = useReportData();
  const { t } = useTranslation();
  const reportNotificationIdRef = useRef(null);

  const FORMAT_OPTIONS = [
    { label: t("365"), value: ReportFormat.WORD },
    { label: t("366"), value: ReportFormat.PDF },
  ];

  const CONTENT_OPTIONS = [
    { label: t("367"), value: "all" },
    { label: t("368"), value: "none" },
  ];

  const SECTION_OPTIONS = [
    { key: "digitalTwin", label: t("369") },
    { key: "check", label: t("370") },
    { key: "align", label: t("371") },
  ];

  const buildSectionsState = isEnabled =>
    SECTION_OPTIONS.reduce((acc, option) => {
      acc[option.key] = isEnabled;
      return acc;
    }, {});

  const [clinicalNotes, setClinicalNotes] = useState("");
  const [contentSelection, setContentSelection] = useState("all");
  const [selectedSections, setSelectedSections] = useState(
    buildSectionsState(true)
  );
  const [format, setFormat] = useState(ReportFormat.WORD);
  const [selectedPlanIds, setSelectedPlanIds] = useState(new Set());
  const [selectedAnnexIds, setSelectedAnnexIds] = useState(new Set());

  const startReportNotification = useCallback(() => {
    if (reportNotificationIdRef.current) {
      removeNotification(reportNotificationIdRef.current);
      reportNotificationIdRef.current = null;
    }

    const id = addNotification(SnackbarType.LOADING, t("376"), {
      duration: 0,
      title: t("359"),
    });
    reportNotificationIdRef.current = id;
  }, [addNotification, removeNotification, t]);

  const completeReportNotification = useCallback(
    (success, message) => {
      if (reportNotificationIdRef.current) {
        updateNotification(reportNotificationIdRef.current, {
          type: success ? SnackbarType.SUCCESS : SnackbarType.ERROR,
          title: success ? t("377") : t("378"),
          message,
          duration: 4000,
        });
        reportNotificationIdRef.current = null;
        return;
      }

      addNotification(success ? SnackbarType.SUCCESS : SnackbarType.ERROR, message, {
        title: success ? t("377") : t("378"),
      });
    },
    [addNotification, t, updateNotification]
  );

  useEffect(() => {
    if (!reportData.allPlans || reportData.allPlans.length === 0) {
      return;
    }

    setSelectedPlanIds(prev => {
      if (prev.size > 0) {
        return prev;
      }

      const selectedPlanId = reportData.selectedPlanId || null;

      if (selectedPlanId) {
        return new Set([selectedPlanId]);
      }

      const firstPlanId = reportData.allPlans[0]?._id;
      if (!firstPlanId) {
        return prev;
      }

      return new Set([firstPlanId]);
    });
  }, [reportData.allPlans, reportData.selectedPlanId]);

  // Count annexes in each section
  const annexCounts = useMemo(() => {
    return {
      digitalTwin: sections[REPORT_SECTIONS.DIGITAL_TWIN]?.length || 0,
      check: sections[REPORT_SECTIONS.CHECK]?.length || 0,
      align: sections[REPORT_SECTIONS.ALIGN]?.length || 0,
      annex: sections[REPORT_SECTIONS.ANNEX]?.length || 0,
    };
  }, [sections, REPORT_SECTIONS]);

  // All screenshots land in the ANNEX bucket regardless of which module captured them
  const allAnnexes = useMemo(
    () => sections[REPORT_SECTIONS.ANNEX] || [],
    [sections, REPORT_SECTIONS]
  );

  const handleClinicalNotesChange = event => {
    setClinicalNotes(event.target.value);
  };

  const handleFormatChange = event => {
    setFormat(Number(event.target.value));
  };

  const handleContentSelectionChange = event => {
    const { value } = event.target;
    setContentSelection(value);

    if (value === "all") {
      setSelectedSections(buildSectionsState(true));
    }

    if (value === "none") {
      setSelectedSections(buildSectionsState(false));
    }
  };

  const handleSectionToggle = key => {
    setSelectedSections(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getPlanDisplayName = useCallback(
    planId => {
      const plans = reportData.allPlans || [];
      const planIndex = plans.findIndex(plan => plan._id === planId);
      const plan = plans[planIndex] || null;

      if (plan?.name) {
        return plan.name;
      }

      if (planIndex >= 0) {
        return `Plan ${planIndex + 1}`;
      }

      return "Plan";
    },
    [reportData.allPlans]
  );

  const handlePlanToggle = planId => {
    setSelectedPlanIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  const handleAnnexToggle = (annex, index) => {
    const id = getAnnexId(annex, `annex-${index}`);
    if (!id) {
      return;
    }
    setSelectedAnnexIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleGenerate = useCallback(async () => {

    // Get selected section names
    const selectedSectionNames = Object.entries(selectedSections)
      .filter(([_, isSelected]) => isSelected)
      .map(([key, _]) => {
        // Map keys to REPORT_SECTIONS names
        const mapping = {
          digitalTwin: "DIGITAL_TWIN",
          check: "CHECK",
          align: "ALIGN",
        };
        return mapping[key] || key.toUpperCase();
      });

    // Always include ANNEX section (it has no checkbox but should always be included)
    if (!selectedSectionNames.includes("ANNEX")) {
      selectedSectionNames.push("ANNEX");
    }

    // Get selected plans from allPlans based on selectedPlanIds
    const selectedPlans = reportData.allPlans.filter(plan =>
      selectedPlanIds.has(plan._id)
    );

    const selectedAnnexes = allAnnexes.filter((annex, index) =>
      selectedAnnexIds.has(getAnnexId(annex, `annex-${index}`))
    );

    // Build the payload
    const payload = buildReportPayload({
      selectedSections: selectedSectionNames,
      selectedPlans,
      clinicalNotes,
      format,
    });


    // Send payload to backend API
    startReportNotification();
    hidePopup();
    try {
      const digitalTwinId = reportData.digitalTwinId;
      const patientId = reportData.patientId;
      if (!digitalTwinId) {
        throw new Error("Digital Twin ID is missing");
      }

      if (selectedAnnexes.length > 0 && patientId) {
        await Promise.all(
          selectedAnnexes
            .filter(annex => annex.blob instanceof Blob)
            .map(annex =>
              uploadAnnexeImageEndpoint(
                patientId,
                digitalTwinId,
                annex.blob,
                annex.fileName
              )
            )
        );
      }

      const result = await prepareReport(payload, digitalTwinId);
      refreshReports();

      // Extract report path from result and trigger download
      if (result) {
        const reportPath = result; // e.g., "/spinesim/data/.../reports/report.docx"
        const fileName = reportPath.split("/").pop();

        if (patientId && fileName) {
          await downloadReport(patientId, fileName);
        } else {
          console.warn("⚠️ Missing patientId or fileName for download", {
            patientId,
            fileName,
          });
        }
      }

      completeReportNotification(true, t("377"));
    } catch (error) {
      completeReportNotification(false, `${t("378")}: ${error.message}`);
    }
  }, [
    allAnnexes,
    clinicalNotes,
    completeReportNotification,
    format,
    hidePopup,
    reportData.allPlans,
    reportData.digitalTwinId,
    reportData.patientId,
    selectedAnnexIds,
    selectedPlanIds,
    refreshReports,
    selectedSections,
    startReportNotification,
    t,
  ]);

  return (
    <ModalBase
      title={t("359")}
      onClose={hidePopup}
      tooltipClose={t("357")}
      className="generate-report-popup"
    >
      <div className="report-wrapper">
        <div className="report-content">
          {/* Clinical Notes Section */}
          <div className="report-section">
            <div className="section-header">
              <h3 className="section-title">{t("360")}</h3>
              <p className="section-subtitle">
                {t("361")}
              </p>
            </div>
            <TextArea
              placeholder={t("362")}
              value={clinicalNotes}
              onChange={handleClinicalNotesChange}
              name="clinicalNotes"
            />
          </div>

          {/* Content Selection Section */}
          <div className="report-section">
            <div className="section-header">
              <h3 className="section-title">{t("363")}</h3>
              <p className="section-subtitle">
                {t("364")}
              </p>
              <div className="format-selector">
                <Select
                  items={FORMAT_OPTIONS}
                  selectedItem={format}
                  onChange={handleFormatChange}
                  name="reportFormat"
                />
              </div>
            </div>

            {/* Radio Buttons */}
            <div className="content-options">
              <RadioButtonFlat
                options={CONTENT_OPTIONS}
                selectedOption={contentSelection}
                onChange={handleContentSelectionChange}
              />
            </div>

            {/* Checkboxes for sections */}
            <div className="section-checkboxes">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={selectedSections.digitalTwin}
                  onChange={() => handleSectionToggle("digitalTwin")}
                />
                <span>
                  {t("369")}
                  {annexCounts.digitalTwin > 0 &&
                    ` (${annexCounts.digitalTwin})`}
                </span>
              </label>
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={selectedSections.check}
                  onChange={() => handleSectionToggle("check")}
                />
                <span>
                  {t("370")}
                  {annexCounts.check > 0 && ` (${annexCounts.check})`}
                </span>
              </label>

              {/* Align section with nested Plan Tabs */}
              <div className="align-section">
                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={selectedSections.align}
                    onChange={() => handleSectionToggle("align")}
                  />
                  <span>
                    {t("371")}
                    {annexCounts.align > 0 && ` (${annexCounts.align})`}
                  </span>
                </label>

                {/* Plan Tabs - shown inside Align when checked */}
                {selectedSections.align && reportData.allPlans.length > 0 && (
                  <div className="plan-tabs">
                    {reportData.allPlans.map(plan => (
                      <Button
                        key={plan._id}
                        value={getPlanDisplayName(plan._id)}
                        isActive={selectedPlanIds.has(plan._id)}
                        onClick={() => handlePlanToggle(plan._id)}
                        className="plan-tab-element"
                      />
                    ))}
                  </div>
                )}
                {selectedSections.align && reportData.allPlans.length === 0 && (
                  <div className="plan-tabs plan-tabs-empty">
                    {t("373")}
                  </div>
                )}
              </div>
            </div>

            {/* Annexes Section */}
            <div className="annexes-section">
              <h4 className="annexes-title">
                {t("374")} {allAnnexes.length > 0 && `(${allAnnexes.length})`}
              </h4>
              <div className="annexes-grid">
                {allAnnexes.length === 0 ? (
                  <div className="annex-placeholder">
                    {t("375")}
                  </div>
                ) : (
                  allAnnexes.map((annex, index) => {
                    // Transform annex to match MedicalImageThumbnail expected format
                    // Convert ISO string to Unix timestamp (milliseconds) for formatDateOnly
                    const dateTimestamp = annex.createdAt
                      ? new Date(annex.createdAt).getTime()
                      : null;

                    const metadata = annex.metadata || {};
                    const rawModality =
                      metadata.modality ||
                      metadata.instanceMetadata?.modality ||
                      "";
                    const modalityLabel = rawModality
                      ? rawModality.toUpperCase()
                      : "";

                    const rawOrientation =
                      metadata.orientation ||
                      metadata.viewType ||
                      metadata.viewName ||
                      "";
                    const orientationLabel =
                      formatOrientationLabel(rawOrientation);

                    const sliceInfo = buildSliceInfo(metadata, modalityLabel);

                    const displayName = [modalityLabel, orientationLabel]
                      .filter(Boolean)
                      .join(" - ");

                    const instance = {
                      url: annex.url,
                      file_name: annex.fileName,
                      date: dateTimestamp,
                      modality: modalityLabel,
                    };
                    const id = getAnnexId(annex, `annex-${index}`);
                    const isSelected = selectedAnnexIds.has(id);

                    return (
                      <button
                        type="button"
                        key={id}
                        className={`annex-thumbnail-item ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={() => handleAnnexToggle(annex, index)}
                        aria-pressed={isSelected}
                      >
                        <MedicalImageThumbnail
                          instance={instance}
                          displayMode="annex"
                          displayData={{
                            name: displayName || undefined,
                            sliceInfo,
                          }}
                          isSelected={isSelected}
                        />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="report-actions">
          <Button
            value={t("377")}
            onClick={handleGenerate}
            className="confirm-button"
          />
        </div>
      </div>
    </ModalBase>
  );
};

export default GenerateReportPopup;
